import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Typography,
  Popover
} from "@material-ui/core";
import { FC, useState } from "react";
import { GenericAppThemeProperty } from "../Types";
import { Edit } from "@material-ui/icons";
import ColorPickerWithAlpha from "src/components/ui-components/ColorPickerWithAlpha";
import { hexToRgba } from "../Helpers";
import { useAppThemeContext } from "src/contexts/AppThemeContext";

type ChartPropertyCardProps = {
  property: GenericAppThemeProperty;
};

const ChartPropertyCard: FC<ChartPropertyCardProps> = ({ property }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { updateGenericColor } = useAppThemeContext(); // Reuse if color update is relevant

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleColorChange = async (
    key: string,
    value: { color: string; alpha: number }
  ) => {
    await updateGenericColor(property.themePropertyKey, "Chart", value.color, value.alpha);
  };

  const displayLabel = property.propertyLabel + (property.index != null ? ` ${property.index}` : "");

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          transition: "all 0.2s ease-in-out",
          "&:hover": { boxShadow: 1, transform: "translateY(-1px)" },
        }}
      >
        <CardContent
          sx={{
            py: 1.5,
            px: 2,
            "&:last-child": { pb: 1.5 },
            "&:first-child": { pt: 1.5 },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box height="100%">
              <Typography variant="subtitle2" fontWeight={600} color="primary">
                {displayLabel}
              </Typography>
              <Box display="flex" flexDirection="row">
                {property.value !== undefined && (
                  <Chip
                    label={property.value}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.75rem", mt: 0.5 }}
                  />
                )}
                {property.alpha !== undefined && (
                  <Chip
                    label={`Opacity: ${property.alpha ?? "1"}`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.75rem", mt: 0.5, marginLeft: "1%" }}
                  />
                )}
              </Box>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  backgroundColor: hexToRgba(property.value, property.alpha ?? 1),
                  border: "1px solid #ccc",
                }}
              />
              <IconButton size="small" onClick={handleOpen}>
                <Edit fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={{ top: window.innerHeight / 2, left: window.innerWidth / 2 }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            p: 1,
            width: "auto",
            maxWidth: 240,
          },
        }}
      >
        <Typography sx={{ marginBottom: '2%' }}>{displayLabel}</Typography>
        <ColorPickerWithAlpha
          propKey={property.themePropertyKey}
          color={property.value}
          alpha={property.alpha ?? 1}
          handleChange={handleColorChange}
        />
      </Popover>
    </>
  );
};

export default ChartPropertyCard;
